import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { getIdTokenResult } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions, selectedAdminEnvironment, getSelectedAdminEnvironmentKey, ADMIN_ENV_STORAGE_KEY } from "../config/firebase";
import { getStaffProvisioningEnvironment } from "../config/staffProvisioningEnvironment";
import { canAccessAdminPortal } from "../utils/adminRoles";
import "./styles/OrderSupportTimeline.css";

const LIMIT = 25;
const SOURCES = { payments: "Payments", refunds: "Refunds", exceptions: "Exceptions", notes: "Notes" };
const STATUSES = {
	payments: { paid: "Paid", partially_refunded: "Partially refunded", refunded: "Refunded" },
	refunds: { pending: "Pending", requires_action: "Requires action", succeeded: "Succeeded", failed: "Failed",
		canceled: "Canceled", needs_review: "Needs review", awaiting_handover: "Awaiting handover", cash_reported: "Cash reported" },
	exceptions: { needs_review: "Needs review", managed_recovery: "Managed recovery", observed: "Observed" },
	notes: { note: "Note", needs_review: "Needs review", customer_contacted: "Customer contacted",
		restaurant_contacted: "Restaurant contacted", resolved: "Resolved" },
};
const validId = value => typeof value === "string" && /^[A-Za-z0-9_-]{1,150}$/.test(value);
const timestamp = value => Number.isFinite(value) && value >= 0 && value <= 253402300799999;
const nullableTime = value => value === null || timestamp(value);
const exact = (value, keys) => value !== null && typeof value === "object" && !Array.isArray(value) &&
	[Object.prototype, null].includes(Object.getPrototypeOf(value)) && Object.keys(value).length === keys.length &&
	keys.every(key => Object.prototype.hasOwnProperty.call(value, key));
const statusLabel = (value, source) => Object.prototype.hasOwnProperty.call(STATUSES[source], value) ? STATUSES[source][value] : "Unknown";
const supportRole = token => {
	const role = token?.claims?.role;
	return typeof role === "string" && role === role.trim() && canAccessAdminPortal(role) ? role : null;
};
const environmentScope = projectId => {
	const environment = getStaffProvisioningEnvironment(selectedAdminEnvironment, projectId);
	return environment && getSelectedAdminEnvironmentKey() === selectedAdminEnvironment.key &&
		functions.app?.options?.projectId === projectId && auth.app?.options?.projectId === projectId ? environment : null;
};

function readPage(value, { projectId, restaurantId, orderId, uid, source }, afterId) {
	const invalid = () => { throw new Error("Unconfirmed order evidence"); };
	if (!exact(value, ["schemaVersion", "firebaseProject", "restaurantId", "orderId", "actorUid", "source",
		"observedAtMs", "coverage", "records", "nextAfterId"]) || value.schemaVersion !== 1 ||
		value.firebaseProject !== projectId || value.restaurantId !== restaurantId || value.orderId !== orderId ||
		value.actorUid !== uid || value.source !== source || value.coverage !== "bounded_page" ||
		!timestamp(value.observedAtMs) || value.observedAtMs === 0 || !Array.isArray(value.records) || value.records.length > LIMIT) invalid();
	let previous = afterId || "";
	const records = value.records.map(row => {
		if (!exact(row, ["id", "identityStatus", "status", "createdAtMs", "updatedAtMs", "amountCents", "currency", "actorUid"]) ||
			!validId(row.id) || row.id <= previous || !["consistent", "needs_review"].includes(row.identityStatus) ||
			typeof row.status !== "string" || row.status.length > 80 ||
			[...row.status].some(character => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127) ||
			!nullableTime(row.createdAtMs) || !nullableTime(row.updatedAtMs) ||
			!(row.amountCents === null || (Number.isSafeInteger(row.amountCents) && row.amountCents >= 0)) ||
			!(row.currency === null || (typeof row.currency === "string" && /^[A-Za-z]{3}$/.test(row.currency))) ||
			!(row.actorUid === null || validId(row.actorUid))) invalid();
		previous = row.id;
		// Only the bounded, validated projection can reach the display state.
		return { id: row.id, identityStatus: row.identityStatus, status: statusLabel(row.status, source),
			createdAtMs: row.createdAtMs, updatedAtMs: row.updatedAtMs, amountCents: row.amountCents,
			currency: row.currency === null ? null : row.currency.toUpperCase(), actorUid: row.actorUid };
	});
	if (value.nextAfterId !== null && (!records.length || !validId(value.nextAfterId) || value.nextAfterId !== previous)) invalid();
	return { records, observedAtMs: value.observedAtMs, nextAfterId: value.nextAfterId };
}

const recordedDate = value => value === null ? "Not recorded" : new Date(value).toISOString();
const recordedAmount = row => row.amountCents === null ? "Unknown amount" : row.currency === null ? "Unknown currency" :
	`${row.currency} ${Math.floor(row.amountCents / 100)}.${String(row.amountCents % 100).padStart(2, "0")}`;

function EvidencePage({ restaurantId, orderId, projectId, uid, user, instance, authInstance, environmentInstance, environment, source, confirmedRole }) {
	const [result, setResult] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const alive = useRef(false), generation = useRef(0), pending = useRef(false);
	const currentScope = useCallback(() => auth === authInstance && auth.currentUser === user && user.uid === uid &&
		functions === instance && selectedAdminEnvironment === environmentInstance && environmentScope(projectId) === environment,
		[authInstance, user, uid, instance, environmentInstance, projectId, environment]);
	const load = useCallback(async (cursors = [null]) => {
		if (pending.current || !alive.current) return;
		const ticket = ++generation.current;
		const active = () => alive.current && ticket === generation.current;
		const current = () => active() && currentScope();
		const history = [...cursors];
		const afterId = history[history.length - 1];
		pending.current = true;
		setLoading(true); setError(""); setResult(null);
		const checkRole = (token, expected = confirmedRole.current) => {
			const role = supportRole(token);
			if (!role || (expected !== null && role !== expected)) {
				confirmedRole.current = null;
				setError(role ? "Support role changed. Reload order evidence." : "Scerv admin access is required.");
				return null;
			}
			return role;
		};
		try {
			if (!current()) return;
			const token = await getIdTokenResult(user, true);
			if (!current()) return;
			const role = checkRole(token);
			if (!role) return;
			const response = await httpsCallable(instance, "getScervOrderSupportTimeline")({
				firebaseProject: projectId, restaurantId, orderId, source, limit: LIMIT, ...(afterId !== null ? { afterId } : {}),
			});
			if (!current()) return;
			const page = readPage(response?.data, { projectId, restaurantId, orderId, uid, source }, afterId);
			const latestToken = await getIdTokenResult(user, true);
			if (!current() || !checkRole(latestToken, role)) return;
			confirmedRole.current = role;
			setResult({ ...page, cursors: history });
		} catch {
			if (current()) setError("Order evidence is unavailable. The response could not be confirmed. Reload to try again.");
		} finally {
			if (active()) {
				if (!currentScope()) setError("Account or environment changed. Reload order evidence.");
				pending.current = false; setLoading(false);
			}
		}
	}, [currentScope, user, instance, projectId, restaurantId, orderId, uid, source, confirmedRole]);
	useEffect(() => {
		alive.current = true;
		load();
		return () => { alive.current = false; generation.current += 1; pending.current = false; };
	}, [load]);
	const page = currentScope() ? result : null;
	return <div className="order-evidence-results" aria-busy={loading}>
		<nav className="order-evidence-pagination" aria-label="Order evidence pages">
			<button type="button" className="order-evidence-icon" aria-label="Previous evidence page" title="Previous evidence page"
				disabled={loading || !page || page.cursors.length < 2} onClick={() => page && load(page.cursors.slice(0, -1))}>&#8592;</button>
			<p role="status">{loading ? "Loading order evidence..." : page ?
				`Page ${page.cursors.length} | ${page.records.length} ${page.records.length === 1 ? "record" : "records"}` : ""}</p>
			<button type="button" className="order-evidence-icon" aria-label="Next evidence page" title="Next evidence page"
				disabled={loading || !page || page.nextAfterId === null}
				onClick={() => page?.nextAfterId && load([...page.cursors, page.nextAfterId])}>&#8594;</button>
			<button type="button" className="order-evidence-icon" aria-label="Reload order evidence" title="Reload order evidence"
				disabled={loading} onClick={() => load()}>&#8635;</button>
		</nav>
		{error && <p role="alert">{error}</p>}
		{page && <p className="order-evidence-meta">Observed {recordedDate(page.observedAtMs)} | Bounded page{page.nextAfterId === null ? " | No further page returned" : ""}</p>}
		{page?.records.length === 0 && <p>No {SOURCES[source].toLowerCase()} records returned for this page.</p>}
		{!!page?.records.length && <div className="order-evidence-scroll" role="region" aria-label={`${SOURCES[source]} evidence records`} tabIndex={0}>
			<table><thead><tr>{["Record / identity", "Recorded status", "Recorded amount", "Recorded created", "Recorded updated", "Recorded actor"].map(label =>
				<th key={label} scope="col">{label}</th>)}</tr></thead>
				<tbody>{page.records.map(row => <tr key={row.id}>
					<td><span className="order-evidence-id">{row.id}</span><small>{row.identityStatus === "consistent" ? "Consistent identity" : "Identity needs review"}</small></td>
					<td>{row.status}</td><td>{recordedAmount(row)}</td>
					<td>{recordedDate(row.createdAtMs)}</td><td>{recordedDate(row.updatedAtMs)}</td>
					<td className="order-evidence-id">{row.actorUid === null ? "Not recorded" : row.actorUid}</td>
				</tr>)}</tbody>
			</table>
		</div>}
	</div>;
}

function ScopedEvidence(props) {
	const [source, setSource] = useState("payments");
	const confirmedRole = useRef(null);
	return <>
		<p className="order-evidence-meta">{props.environment} | {props.projectId} | Restaurant {props.restaurantId} | Order {props.orderId}</p>
		<div className="order-evidence-sources" role="group" aria-label="Evidence source">
			{Object.entries(SOURCES).map(([value, label]) => <button type="button" key={value} aria-pressed={source === value}
				onClick={() => setSource(value)}>{label}</button>)}
		</div>
		<EvidencePage key={source} {...props} source={source} confirmedRole={confirmedRole} />
	</>;
}

export default function OrderSupportTimeline({ restaurantId, orderId, projectId }) {
	const [user, loading, error] = useAuthState(auth);
	const session = useRef({ revision: 0 });
	if (session.current.user !== user || session.current.auth !== auth || session.current.functions !== functions ||
		session.current.environment !== selectedAdminEnvironment) {
		session.current = { user, auth, functions, environment: selectedAdminEnvironment, revision: session.current.revision + 1 };
	}
	const [, refreshEnvironment] = useState(0);
	useEffect(() => {
		const changed = event => {
			if (event.key === ADMIN_ENV_STORAGE_KEY || event.key === null) refreshEnvironment(value => value + 1);
		};
		window.addEventListener("storage", changed);
		return () => window.removeEventListener("storage", changed);
	}, []);
	const environment = environmentScope(projectId);
	let content;
	if (loading) content = <p role="status">Checking support access...</p>;
	else if (error || !user || !validId(user.uid) || auth.currentUser !== user) content = <p role="alert">Sign in with an authorized support account.</p>;
	else if (!environment || !validId(restaurantId) || !validId(orderId)) content = <p role="alert">Order evidence scope does not match the active Firebase project or order. Reload before continuing.</p>;
	else content = <ScopedEvidence key={JSON.stringify([projectId, restaurantId, orderId, environment, user.uid, session.current.revision])}
		restaurantId={restaurantId} orderId={orderId} projectId={projectId} uid={user.uid} user={user} instance={functions}
		authInstance={auth} environmentInstance={selectedAdminEnvironment} environment={environment} />;
	return <section className="order-evidence" aria-label="Order evidence">
		<h2>Order evidence</h2>
		<p className="order-evidence-notice">Bounded recorded evidence by source, ordered by record ID. This is not a full chronological timeline or proof of a provider outcome.</p>
		{content}
	</section>;
}
